import React from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { FormInstance } from "antd/es/form";
import type { UploadFile } from "antd/es/upload/interface";
import { UploadOutlined } from "@ant-design/icons";
import type { Booking } from "../../types/booking";
import type { EstimatedTransportResult } from "../../services/pricingCalculationService";
import {
  LEANCUSTOMS_AGENT_ID,
  LEANCUSTOMS_EFREIGHT_ORG_ID,
} from "../../constants/leancustoms";
import {
  LEANCUSTOMS_ORDER_COST_LABELS_NL,
  type OrderCostAttribute,
} from "../../utils/leancustomsOrderCosts";
import {
  LEANCUSTOMS_EXPORT_CLEARANCE_COST,
  originThcNetOfExportClearance,
  type LeanCustomsFreightCostsBreakdown,
} from "../../utils/leanCustomsFreightCosts";
import type { BookingOtherDocumentRow } from "../../services/leanCustomsAttachmentService";
import { otherDocumentDisplayName } from "../../services/leanCustomsAttachmentService";
import { LeanCustomsStep1ReadinessCard, getLeanCustomsReadinessState, READINESS_ATTENTION_STYLE } from "./LeanCustomsWizardReadiness";
import { LeanCustomsNumberedSection } from "./LeanCustomsNumberedSection";

const EXTRA_ATTACHMENT_MAX_MB = 10;

export type LeanCustomsCreateOrderFormSectionsProps = {
  form: FormInstance;
  booking: Booking;
  bookingRef: string;
  verifiedCommercialInvoice: boolean;
  formValues: Record<string, unknown>;
  hasTariff: boolean;
  manualEstimatedShipping: number;
  estimatedTransportLoading: boolean;
  requiresShippingEstimate: boolean;
  isAir: boolean;
  isSeafreight: boolean;
  isFCL: boolean;
  transportMode: string;
  incotermCode: string;
  defaultConsolidationLevel: string;
  containerPorts: { originPortInfo: unknown; destinationPortInfo: unknown };
  origCountry: string;
  destCountry: string;
  grossWeight: number;
  invoiceAmount: number;
  totalVolume: number;
  orgId: string;
  chargeableWeight: number;
  volumeWeight: number;
  estimatedTransport: EstimatedTransportResult | null;
  effectiveEstimatedShipping: number;
  effectiveFreightCosts: number;
  freightCostsFromBooking: number;
  freightCostsBreakdown: LeanCustomsFreightCostsBreakdown;
  fclOriginThcLoading: boolean;
  originTerminalHandling: number | null;
  resolvedFclContainerSizeId: number | null;
  previewOrderCosts: OrderCostAttribute[];
  orderDetails: string;
  extraAttachmentList: UploadFile[];
  onExtraAttachmentListChange: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  otherDocuments: BookingOtherDocumentRow[];
  otherDocumentsLoading: boolean;
  selectedOtherDocumentIds: number[];
  onSelectedOtherDocumentIdsChange: React.Dispatch<React.SetStateAction<number[]>>;
  submitting: boolean;
  getPortDisplay: (portInfo: unknown) => string;
  getTransportTypeName: () => string;
  getPortName: (portInfo: unknown) => string;
  getTariffDate: () => string;
  exportClearanceCost: number;
  onAttachmentTooLarge: (filename: string) => void;
};

export const LeanCustomsCreateOrderFormSections: React.FC<
  LeanCustomsCreateOrderFormSectionsProps
> = ({
  form,
  booking,
  bookingRef,
  verifiedCommercialInvoice,
  formValues,
  hasTariff,
  manualEstimatedShipping,
  estimatedTransportLoading,
  requiresShippingEstimate,
  isAir,
  isSeafreight,
  isFCL,
  transportMode,
  incotermCode,
  defaultConsolidationLevel,
  containerPorts,
  origCountry,
  destCountry,
  grossWeight,
  invoiceAmount,
  totalVolume,
  orgId,
  chargeableWeight,
  volumeWeight,
  estimatedTransport,
  effectiveEstimatedShipping,
  effectiveFreightCosts,
  freightCostsFromBooking,
  freightCostsBreakdown,
  fclOriginThcLoading,
  originTerminalHandling,
  resolvedFclContainerSizeId,
  previewOrderCosts,
  orderDetails,
  extraAttachmentList,
  onExtraAttachmentListChange,
  otherDocuments,
  otherDocumentsLoading,
  selectedOtherDocumentIds,
  onSelectedOtherDocumentIdsChange,
  submitting,
  getPortDisplay,
  getTransportTypeName,
  getPortName,
  getTariffDate,
  exportClearanceCost,
  onAttachmentTooLarge,
}) => {
  const readiness = getLeanCustomsReadinessState({
    formValues,
    hasCommercialInvoice: verifiedCommercialInvoice,
    hasTariff,
    manualShipping: manualEstimatedShipping,
    estimatedTransportLoading,
    requiresShippingEstimate,
  });
  const shippingNeedsAttention = !readiness.shippingOk && requiresShippingEstimate;
  const fclTariffOptions = estimatedTransport?.fclTariffOptions ?? [];
  const needsFclSizeChoice = isFCL && fclTariffOptions.length > 1;

  /** Unit rate from tariff lookup (€/kg, €/CBM, or €/container) — not the shipping total. */
  const tariffRateLabel = (() => {
    const rate = estimatedTransport?.tariffRate;
    if (rate == null || Number.isNaN(rate)) return null;
    const formatted = `€${rate.toFixed(2)}`;
    const unit = estimatedTransport?.tariffUnit;
    if (unit === "per_kg") return `${formatted}/kg`;
    if (unit === "per_cbm") return `${formatted}/CBM`;
    if (unit === "per_container") return formatted;
    if (isAir) return `${formatted}/kg`;
    if (isSeafreight && !isFCL) return `${formatted}/CBM`;
    return formatted;
  })();

  return (
    <>
      <div
        style={{
          fontSize: 12,
          color: "#595959",
          padding: "8px 12px",
          background: !readiness.invoiceOk ? "#fff7e6" : "#fafafa",
          borderRadius: 6,
          border: !readiness.invoiceOk ? "1px solid #ffa940" : "1px solid #f0f0f0",
          marginBottom: 16,
          lineHeight: 1.6,
        }}
      >
        <strong>{bookingRef}</strong> · {incotermCode} ·{" "}
        {defaultConsolidationLevel === "full_load" ? "FCL" : "LCL"} · {transportMode}
        {isSeafreight && totalVolume > 0 ? ` · ${totalVolume.toFixed(2)} m³` : ""}
        <span style={{ color: "#8c8c8c" }}>
          {" "}
          · {grossWeight || "—"} kg · €{invoiceAmount ?? "—"} ·{" "}
          {getPortDisplay(containerPorts.originPortInfo) || "—"} ({origCountry}) →{" "}
          {getPortDisplay(containerPorts.destinationPortInfo) || "—"} ({destCountry})
        </span>
      </div>

      {!verifiedCommercialInvoice && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, border: "1px solid #ffa940", background: "#fff7e6" }}
          message="Commercial invoice required on booking"
        />
      )}

      <LeanCustomsStep1ReadinessCard
        formValues={formValues}
        hasCommercialInvoice={verifiedCommercialInvoice}
        hasTariff={hasTariff}
        manualShipping={manualEstimatedShipping}
        estimatedTransportLoading={estimatedTransportLoading}
        isAir={isAir}
        requiresShippingEstimate={requiresShippingEstimate}
      />

      <LeanCustomsNumberedSection number={1} title="Order" attention={!readiness.docOk}>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="order_type" label="Import / export">
              <Select
                options={[
                  { value: "import", label: "Import" },
                  { value: "export", label: "Export" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="consolidation_level" label="Load type">
              <Select
                disabled={isAir}
                options={[
                  { value: "full_load", label: "FCL — full container" },
                  { value: "partial_load", label: "LCL — shared load" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="estimated_arrival" label="ETA destination">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="document_number"
              label="AWB / B/L number"
              validateStatus={!readiness.docOk ? "warning" : undefined}
              rules={[
                { required: true, message: "Required" },
                { pattern: /\S/, message: "Cannot be blank" },
              ]}
            >
              <Input placeholder="MAWB or B/L" />
            </Form.Item>
          </Col>
        </Row>
      </LeanCustomsNumberedSection>

      <LeanCustomsNumberedSection number={2} title="Costs" attention={shippingNeedsAttention}>
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <div
              style={
                shippingNeedsAttention
                  ? { ...READINESS_ATTENTION_STYLE, padding: "10px 12px" }
                  : undefined
              }
            >
            <Typography.Text
              type="secondary"
              style={{
                display: "block",
                marginBottom: 10,
                fontSize: 12,
                color: shippingNeedsAttention ? "#d46b08" : undefined,
              }}
            >
              Shipping estimate
            </Typography.Text>
            {estimatedTransportLoading ? (
              <Typography.Text type="secondary">Loading tariff…</Typography.Text>
            ) : needsFclSizeChoice ? (
              <>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
                  Multiple FCL tariffs found — select container size:
                </Typography.Text>
                <Form.Item
                  name="selected_fcl_container_size_id"
                  style={{ marginBottom: 10 }}
                  validateStatus={shippingNeedsAttention ? "warning" : undefined}
                >
                  <Radio.Group>
                    <Space direction="vertical">
                      {fclTariffOptions.map((option) => (
                        <Radio key={String(option.containerSizeId)} value={option.containerSizeId}>
                          {option.containerSizeName} — €{option.amount.toFixed(2)}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </>
            ) : hasTariff ? (
              <Tag color="green" style={{ marginBottom: 10 }}>
                Tariff {tariffRateLabel ?? `€${effectiveEstimatedShipping.toFixed(2)}`}
              </Tag>
            ) : (
              <Space wrap style={{ marginBottom: 10 }}>
                {isAir && (
                  <>
                    <Form.Item
                      name="price_per_kg"
                      label="€/kg"
                      style={{ marginBottom: 0 }}
                      validateStatus={shippingNeedsAttention ? "warning" : undefined}
                    >
                      <InputNumber min={0} step={0.01} precision={2} prefix="€" style={{ width: 120 }} />
                    </Form.Item>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {chargeableWeight.toFixed(2)} kg
                    </Typography.Text>
                  </>
                )}
                {isSeafreight && !isFCL && (
                  <>
                    <Form.Item
                      name="price_per_cbm"
                      label="€/CBM"
                      style={{ marginBottom: 0 }}
                      validateStatus={shippingNeedsAttention ? "warning" : undefined}
                    >
                      <InputNumber min={0} step={0.01} precision={2} prefix="€" style={{ width: 120 }} />
                    </Form.Item>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {totalVolume.toFixed(2)} CBM
                    </Typography.Text>
                  </>
                )}
                {isSeafreight && isFCL && !needsFclSizeChoice && (
                  <Form.Item
                    name="price_per_container"
                    label="€/container"
                    style={{ marginBottom: 0 }}
                    validateStatus={shippingNeedsAttention ? "warning" : undefined}
                  >
                    <InputNumber min={0} step={0.01} precision={2} prefix="€" style={{ width: 140 }} />
                  </Form.Item>
                )}
                {isSeafreight && isFCL && needsFclSizeChoice && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Or use the override field below
                  </Typography.Text>
                )}
              </Space>
            )}
            <Form.Item
              name="estimated_shipping_override"
              label="Override"
              style={{ marginBottom: 8 }}
              validateStatus={shippingNeedsAttention ? "warning" : undefined}
            >
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                style={{ width: "100%" }}
                placeholder="Optional"
                prefix="€"
              />
            </Form.Item>
            <Typography.Text
              style={{
                fontSize: 12,
                color: readiness.shippingOk || !requiresShippingEstimate ? "#389e0d" : "#d46b08",
              }}
            >
              {readiness.shippingOk || !requiresShippingEstimate
                ? hasTariff && tariffRateLabel && isAir
                  ? `Uses €${effectiveEstimatedShipping.toFixed(2)} (${chargeableWeight.toFixed(2)} kg × ${tariffRateLabel})`
                  : `Uses €${effectiveEstimatedShipping.toFixed(2)}`
                : "Enter rate or override"}
            </Typography.Text>
            <Collapse
              size="small"
              ghost
              style={{ marginTop: 8 }}
              items={[
                {
                  key: "tariff",
                  label: <span style={{ fontSize: 12 }}>Tariff lookup</span>,
                  children: (
                    <Descriptions size="small" column={1} bordered style={{ fontSize: 12 }}>
                      <Descriptions.Item label="Transport">{getTransportTypeName()}</Descriptions.Item>
                      <Descriptions.Item label="Route">
                        {getPortName(containerPorts.originPortInfo)} →{" "}
                        {getPortName(containerPorts.destinationPortInfo)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tariff date">{getTariffDate()}</Descriptions.Item>
                      <Descriptions.Item label="Found">
                        {needsFclSizeChoice ? (
                          <Space direction="vertical" size={4}>
                            {fclTariffOptions.map((option) => (
                              <Tag key={String(option.containerSizeId)} color="blue">
                                {option.containerSizeName}: €{option.amount.toFixed(2)}
                              </Tag>
                            ))}
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                              Select size above
                            </Typography.Text>
                          </Space>
                        ) : hasTariff ? (
                          <Tag color="green">
                            Yes — {tariffRateLabel ?? `€${effectiveEstimatedShipping.toFixed(2)}`}
                          </Tag>
                        ) : (
                          <Tag color="orange">No</Tag>
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  ),
                },
              ]}
            />
            </div>
          </Col>

          <Col xs={24} lg={12}>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 10, fontSize: 12 }}>
              {isFCL && isSeafreight
                ? "Laadplaats (pickup + origin THC)"
                : "Laadplaats (pickup + export clearance)"}
            </Typography.Text>
            <Typography.Text style={{ display: "block", marginBottom: 8 }}>
              From booking: €{freightCostsFromBooking.toFixed(2)}
              {fclOriginThcLoading ? " …" : ""}
            </Typography.Text>
            <Form.Item name="freight_costs_override" label="Override" style={{ marginBottom: 8 }}>
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                style={{ width: "100%" }}
                placeholder="Optional"
                prefix="€"
              />
            </Form.Item>
            <Typography.Text type="success" style={{ fontSize: 12 }}>
              Uses €{effectiveFreightCosts.toFixed(2)}
            </Typography.Text>
            {isFCL && isSeafreight && !fclOriginThcLoading && originTerminalHandling == null && (
              <Typography.Text type="warning" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                {resolvedFclContainerSizeId == null
                  ? "Select container size to include origin THC"
                  : "No origin THC found for this container size"}
              </Typography.Text>
            )}
            <Collapse
              size="small"
              ghost
              style={{ marginTop: 8 }}
              items={[
                {
                  key: "pickup",
                  label: <span style={{ fontSize: 12 }}>Pickup breakdown</span>,
                  children: (
                    <Descriptions size="small" column={1} bordered>
                      <Descriptions.Item label="Pickup fee">
                        {booking.pick_up_fee != null ? `€${booking.pick_up_fee.toFixed(2)}` : "—"}
                      </Descriptions.Item>
                      {isFCL && isSeafreight ? (
                        <>
                          <Descriptions.Item label="Origin THC">
                            {originTerminalHandling != null
                              ? `€${originTerminalHandling.toFixed(2)} (incl. EC €${LEANCUSTOMS_EXPORT_CLEARANCE_COST} → net €${originThcNetOfExportClearance(originTerminalHandling).toFixed(2)})`
                              : fclOriginThcLoading
                                ? "Loading…"
                                : resolvedFclContainerSizeId == null
                                  ? "Select container size"
                                  : "—"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Export clearance">
                            {freightCostsBreakdown.exportClearanceIncludedInThc
                              ? "Included in origin THC (not added again)"
                              : booking.export_clearance === true
                                ? `Yes — €${exportClearanceCost.toFixed(2)}`
                                : booking.export_clearance === false
                                  ? "No"
                                  : "—"}
                          </Descriptions.Item>
                        </>
                      ) : (
                        <Descriptions.Item label="Export clearance">
                          {booking.export_clearance === true
                            ? `Yes — €${exportClearanceCost.toFixed(2)}`
                            : booking.export_clearance === false
                              ? "No"
                              : "—"}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  ),
                },
              ]}
            />
          </Col>
        </Row>

        {previewOrderCosts.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
              Sent to Lean Customs
            </Typography.Text>
            <Row gutter={[12, 8]}>
              {previewOrderCosts.map((cost) => (
                <Col xs={24} sm={12} key={cost.cost_type}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      padding: "4px 0",
                    }}
                  >
                    <span>{LEANCUSTOMS_ORDER_COST_LABELS_NL[cost.cost_type]}</span>
                    <strong>€{cost.amount.toFixed(2)}</strong>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </LeanCustomsNumberedSection>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <LeanCustomsNumberedSection number={3} title="Extra bijlagen" style={{ height: "calc(100% - 16px)" }}>
            {otherDocumentsLoading ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Loading booking documents…
              </Typography.Text>
            ) : otherDocuments.length > 0 ? (
              <>
                <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 8 }}>
                  From booking — other documents
                </Typography.Text>
                <Checkbox.Group
                  value={selectedOtherDocumentIds}
                  onChange={(ids) => onSelectedOtherDocumentIdsChange(ids as number[])}
                  style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}
                >
                  {otherDocuments.map((doc) => (
                    <Checkbox key={doc.id} value={doc.id} disabled={submitting}>
                      {otherDocumentDisplayName(doc.booking_other_documents_file)}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </>
            ) : null}
            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 10 }}>
              Add new files (saved to booking + uploaded to Lean Customs).
            </Typography.Text>
            <Upload
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              fileList={extraAttachmentList}
              beforeUpload={(file) => {
                if (file.size / 1024 / 1024 >= EXTRA_ATTACHMENT_MAX_MB) {
                  onAttachmentTooLarge(file.name);
                  return Upload.LIST_IGNORE;
                }
                onExtraAttachmentListChange((prev) => [
                  ...prev,
                  { uid: file.uid, name: file.name, status: "done", originFileObj: file },
                ]);
                return false;
              }}
              onRemove={(file) => {
                onExtraAttachmentListChange((prev) => prev.filter((f) => f.uid !== file.uid));
              }}
            >
              <Button icon={<UploadOutlined />} disabled={submitting} size="small">
                Add file
              </Button>
            </Upload>
          </LeanCustomsNumberedSection>
        </Col>

        <Col xs={24} lg={12}>
          <LeanCustomsNumberedSection number={4} title="Order message" style={{ height: "calc(100% - 16px)" }}>
            <Form.Item name="details" style={{ marginBottom: 8 }}>
              <Input.TextArea rows={3} placeholder="Auto-filled from booking" />
            </Form.Item>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => form.setFieldsValue({ details: orderDetails })}>
              Reset to auto-generated
            </Button>
          </LeanCustomsNumberedSection>
        </Col>
      </Row>

      <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: -4 }}>
        Declarant {orgId || "—"} · Forwarder {LEANCUSTOMS_EFREIGHT_ORG_ID} · Agent {LEANCUSTOMS_AGENT_ID}
      </Typography.Text>
    </>
  );
};
